import { generateUUID } from '@grafana/data';
import { AnnoKeyCreatedBy } from 'app/features/apiserver/types';
import { type SavedQueryBase } from 'app/features/explore/QueryLibrary/types';

import { type ListQueryApiResponse, type Query as QT } from '../../api/clients/queries/v1';
import { type AddQueryTemplateCommand } from '../types';

// Flatten a single Query resource (metadata + spec) into the frontend SavedQueryBase shape.
export const convertQueryToSavedQueryBase = (query: QT): SavedQueryBase => {
  return {
    uid: query.metadata?.name ?? '',
    title: query.spec?.title ?? '',
    description: query.spec?.description ?? '',
    tags: query.spec?.tags ?? [],
    isLocked: query.spec?.isLocked ?? false,
    isVisible: query.spec?.isVisible ?? false,
    targets:
      query.spec?.targets.map((target) => ({
        ...target.properties,
        refId: target.properties.refId ?? '',
      })) ?? [],
    createdAtTimestamp: new Date(query.metadata?.creationTimestamp ?? '').getTime(),
    user: {
      uid: query.metadata?.annotations?.[AnnoKeyCreatedBy] ?? '',
    },
  };
};

export const convertDataQueryResponseToSavedQueriesDTO = (result: ListQueryApiResponse): SavedQueryBase[] => {
  if (!result.items) {
    return [];
  }
  return result.items.map(convertQueryToSavedQueryBase);
};

export const convertAddQueryTemplateCommandToDataQuerySpec = (addQueryTemplateCommand: AddQueryTemplateCommand): QT => {
  const { title, targets, description, isLocked, isVisible, tags } = addQueryTemplateCommand;
  return {
    metadata: {
      /**
       * Server will append to whatever is passed here, but just to be safe we generate a uuid
       * More info https://github.com/kubernetes/community/blob/master/contributors/devel/sig-architecture/api-conventions.md#idempotency
       */
      generateName: generateUUID(),
    },
    spec: {
      title,
      description,
      isVisible,
      vars: [], // TODO: Detect variables in #86838
      tags,
      targets: targets.map((dataQuery) => ({
        variables: {},
        properties: {
          ...dataQuery,
          datasource: {
            ...dataQuery.datasource,
            type: dataQuery.datasource?.type ?? '',
          },
        },
      })),
      isLocked: isLocked ?? false,
    },
  };
};

export const convertToMapTagCount = (loadQueryMetadataResult: { loading: boolean; value: any }) => {
  if (!loadQueryMetadataResult.value) {
    return [];
  }

  const tagCounts = new Map<string, number>();

  // Count occurrences of each tag
  loadQueryMetadataResult.value.forEach((row: { tags: string[] }) => {
    row.tags.forEach((tag) => {
      tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
    });
  });

  // Convert to TermCount array and sort by count (descending) then by name
  return Array.from(tagCounts.entries())
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => {
      if (a.count !== b.count) {
        return b.count - a.count; // Sort by count descending
      }
      return a.term.localeCompare(b.term); // Then by name ascending
    });
};
